import { z } from 'zod';

const FORMAT_MESSAGES: Record<string, string> = {
  email: 'Enter a valid email address',
  url: 'Enter a valid URL, e.g. https://example.com',
  uuid: 'Enter a valid ID',
  datetime: 'Enter a valid date and time',
  date: 'Enter a valid date',
  time: 'Enter a valid time',
  regex: 'Use the expected format',
};

const EXPECTED_MESSAGES: Record<string, string> = {
  number: 'Enter a number',
  int: 'Enter a whole number',
  string: 'Enter text',
  boolean: 'Choose yes or no',
  array: 'Choose at least one option',
  date: 'Enter a valid date',
};

/**
 * Plain-language messages for the issues the generated schemas raise, e.g. "Required" instead of
 * "Invalid input: expected string, received undefined". Returning undefined keeps zod's own message.
 */
export const friendlyZodMessage: z.core.$ZodErrorMap = (issue) => {
  // An empty input is a missing value, whatever the rule it then fails (format, length, enum).
  if (issue.input === '') return 'Required';
  switch (issue.code) {
    case 'invalid_type':
      if (issue.input === undefined || issue.input === null) return 'Required';
      return EXPECTED_MESSAGES[issue.expected] ?? 'Enter a valid value';
    case 'too_small': {
      const min = Number(issue.minimum);
      if (issue.origin === 'string') return min <= 1 ? 'Required' : `Enter at least ${min} characters`;
      if (issue.origin === 'array' || issue.origin === 'set') return `Choose at least ${min}`;
      return issue.inclusive ? `Must be at least ${min}` : `Must be greater than ${min}`;
    }
    case 'too_big': {
      const max = Number(issue.maximum);
      if (issue.origin === 'string') return `Use at most ${max} characters`;
      if (issue.origin === 'array' || issue.origin === 'set') return `Choose at most ${max}`;
      return issue.inclusive ? `Must be at most ${max}` : `Must be less than ${max}`;
    }
    case 'invalid_format':
      return FORMAT_MESSAGES[issue.format] ?? 'Use the expected format';
    case 'invalid_value':
      return 'Choose one of the options';
    case 'not_multiple_of':
      return `Must be a multiple of ${String(issue.divisor)}`;
    default:
      return undefined;
  }
};

/** Installs the messages for every schema in the app. Called once from main.tsx. */
export function installFriendlyZodMessages(): void {
  z.config({ customError: friendlyZodMessage });
}
